import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle2, Award, PawPrint, ArrowRight } from 'lucide-react';
import { siteImages } from '../data/mock';

const QualityGuaranteePage = () => {
  useEffect(() => {
    document.title = 'Quality Guarantee | Paw Supplies Group';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main>
        {/* Banner */}
        <section className="relative h-64 md:h-80 overflow-hidden">
          <img 
            src={siteImages.featuredBackground}
            alt="Quality Guarantee"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-brown/85 via-brand-brown/60 to-brand-brown/30" />
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-brand-cream mb-2">
                <Shield className="w-5 h-5" />
                <span className="text-xs font-bold uppercase tracking-[0.25em]">Our Commitment</span>
              </div>
              <h1 className="text-4xl md:text-5xl text-white font-extrabold mb-3" className-FONT-DISPLAY>
                Quality Guarantee
              </h1>
              <p className="text-brand-cream max-w-lg">
                Our commitment to providing the finest pet accessories for your furry family.
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Main Guarantee */}
          <div className="bg-brand-cream border border-brand-divider rounded-3xl p-8 md:p-12 mb-16 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-brand-orange to-brand-orange-dark rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Award className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl lg:text-4xl text-brand-brown font-extrabold mb-6" className-FONT-DISPLAY>
              Our Quality Promise
            </h2>
            <p className="text-brand-brown-light text-lg leading-relaxed max-w-3xl mx-auto">
              At Paw Supplies Group, we stand behind every pet product we sell. We carefully select all of our beds, feeders, toys, carriers, collars and leashes to ensure they meet our high standards for comfort, durability and pet-first design. If you and your pet aren&apos;t completely happy, we&apos;ll make it right.
            </p>
          </div>

          {/* Quality Standards */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              { icon: Shield, title: 'Tested & Trusted', desc: 'Every product is reviewed for quality and pet safety before joining our catalog.' },
              { icon: CheckCircle2, title: '30-Day Returns', desc: 'Not satisfied? Return any unused item within 30 days for a full refund.' },
              { icon: Award, title: 'Premium Quality', desc: 'We only offer pet accessories that meet our strict comfort and quality standards.' },
            ].map((item, i) => (
              <div key={i} className="bg-brand-cream border border-brand-divider rounded-2xl p-6 text-center hover:border-brand-orange/40 transition-all group">
                <div className="w-14 h-14 bg-brand-orange-light/30 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-brand-orange-light/50 transition-colors">
                  <item.icon className="w-7 h-7 text-brand-orange-dark" />
                </div>
                <h3 className="font-bold text-brand-brown text-lg mb-2" className-FONT-DISPLAY>{item.title}</h3>
                <p className="text-brand-brown-light text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* What We Guarantee */}
          <div className="bg-brand-cream border border-brand-divider rounded-3xl p-8 md:p-12 mb-16">
            <h2 className="text-2xl font-bold text-brand-brown mb-8 text-center" className-FONT-DISPLAY>
              What We Guarantee
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Products as described on our website',
                'Pet-safe materials and construction',
                'Fast, secure shipping',
                '30-day money-back guarantee',
                'Secure payment processing',
                'Responsive customer support',
                'Original products, no counterfeits',
                'Safe packaging and handling',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-white rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange flex-shrink-0" />
                  <span className="text-brand-brown font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-gradient-to-br from-brand-cream to-brand-orange-light/20 border border-brand-divider rounded-3xl p-12">
            <h2 className="text-3xl font-extrabold text-brand-brown mb-4" className-FONT-DISPLAY>
              Shop with Confidence
            </h2>
            <p className="text-brand-brown-light mb-8 max-w-xl mx-auto">
              Experience our commitment to quality. Every purchase is backed by our pet-parent satisfaction guarantee.
            </p>
            <Link 
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand-orange text-white font-bold uppercase tracking-wider rounded-full hover:bg-brand-orange transition-all shadow-lg"
            >
              <PawPrint className="w-5 h-5" />
              Shop Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default QualityGuaranteePage;
